import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { CountdownToLaunch } from '../components/CountdownToLaunch';
import { toast } from 'sonner';
import {
    Wallet, Sparkles, Trophy, Ticket, Crown, Coins, TrendingUp,
    Loader2, Check, Star, Users, Flame, Gift, ArrowRight, Info,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ETH_PRICE_USD = 4000; // displayed approximation

const TIER_STYLES = {
    diamond_founder: { color: '#00E5FF', bg: 'bg-[#00E5FF]/10', border: 'border-[#00E5FF]/30', icon: Crown, labelKey: 'presale.tierDiamond' },
    gold_founder: { color: '#FFD700', bg: 'bg-[#FFD700]/10', border: 'border-[#FFD700]/30', icon: Trophy, labelKey: 'presale.tierGold' },
    silver_founder: { color: '#C0C0C0', bg: 'bg-white/10', border: 'border-white/20', icon: Star, labelKey: 'presale.tierSilver' },
    bronze_founder: { color: '#CD7F32', bg: 'bg-[#CD7F32]/10', border: 'border-[#CD7F32]/30', icon: Star, labelKey: 'presale.tierBronze' },
    early_holder: { color: '#9B6FE8', bg: 'bg-[#9B6FE8]/10', border: 'border-[#9B6FE8]/30', icon: Sparkles, labelKey: 'presale.tierEarly' },
};

function formatAddr(addr) {
    if (!addr) return '';
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function StatBox({ icon: Icon, label, value, accent = '#FFD700', testId }) {
    return (
        <Card className="bg-white/[0.04] border-white/10" data-testid={testId}>
            <CardContent className="p-5">
                <Icon className="w-5 h-5 mb-2" style={{ color: accent }} />
                <div className="text-2xl sm:text-3xl font-black text-white font-mono tabular-nums">{value}</div>
                <div className="text-[10px] text-white/50 uppercase tracking-wider mt-1">{label}</div>
            </CardContent>
        </Card>
    );
}

function BuyForm({ config, userInfo, onPurchased }) {
    const { t } = useTranslation();
    const { isConnected, address, connect } = useWeb3();
    const [currency, setCurrency] = useState('ETH');
    const [amount, setAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const rate = currency === 'ETH' ? config?.rates?.b4p_per_eth : config?.rates?.b4p_per_usdc;
    const isWhitelisted = userInfo?.whitelisted;
    const numericAmount = parseFloat(amount) || 0;
    const baseB4P = numericAmount * (rate || 0);
    const bonusB4P = isWhitelisted ? baseB4P * 0.3 : 0;
    const totalB4P = baseB4P + bonusB4P;
    const ticketRate = currency === 'ETH' ? config?.lottery?.ticket_eth : config?.lottery?.ticket_usdc;
    // Integer math to dodge IEEE-754 truncation (0.3 / 0.1 = 2.9999…)
    const tickets = ticketRate
        ? Math.floor(Math.round(numericAmount * 1e6) / Math.round(ticketRate * 1e6))
        : 0;
    const maxPerWallet = currency === 'ETH' ? config?.caps?.max_per_wallet_eth : config?.caps?.max_per_wallet_usdc;
    const contractDeployed = config?.contract_deployed;

    async function handleBuy() {
        if (!isConnected) { await connect(); return; }
        if (numericAmount <= 0) {
            toast.error(t('presale.invalidAmount'));
            return;
        }
        setSubmitting(true);
        try {
            // NOTE: This optimistically records the contribution server-side.
            // When B4PPresale.sol is deployed, a real wagmi transaction will fire first,
            // and the server endpoint will become an indexer-driven update.
            const res = await fetch(`${API}/api/presale/record`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    address, currency, amount: numericAmount,
                    tx_hash: contractDeployed ? null : `mock_${Date.now()}_${address.slice(2, 10)}`,
                }),
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            toast.success(t('presale.purchaseRecorded'), {
                description: `${data.contribution.b4p_amount.toLocaleString()} B4P · ${data.contribution.lottery_tickets} ${t('presale.tickets')}`,
            });
            setAmount('');
            onPurchased?.();
        } catch (e) {
            toast.error(t('presale.purchaseFailed'), { description: String(e).slice(0, 100) });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Card className="bg-gradient-to-br from-[#FFD700]/10 via-white/[0.04] to-[#0066CC]/10 border-[#FFD700]/20" data-testid="presale-buy-form">
            <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-2">
                    <Wallet className="w-6 h-6 text-[#FFD700]" />
                    {t('presale.buyTitle')}
                </CardTitle>
                {!contractDeployed && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/30 text-[#FFD700] text-xs font-semibold w-fit">
                        <Info className="w-3 h-3" />
                        {t('presale.preDeployMode')}
                    </div>
                )}
            </CardHeader>
            <CardContent className="space-y-5">
                {/* Currency selector */}
                <div className="grid grid-cols-2 gap-2">
                    {['ETH', 'USDC'].map((c) => (
                        <button
                            type="button"
                            key={c}
                            onClick={() => setCurrency(c)}
                            data-testid={`presale-currency-${c.toLowerCase()}`}
                            className={`p-3 rounded-lg font-bold transition-all border ${
                                currency === c
                                    ? 'bg-[#FFD700] text-[#001A4D] border-[#FFD700]'
                                    : 'bg-white/5 text-white border-white/10 hover:bg-white/10'
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>

                {/* Amount */}
                <div>
                    <label htmlFor="presale-amount" className="text-xs text-white/60 uppercase tracking-wider mb-1.5 block">
                        {t('presale.amountLabel', { ccy: currency })}
                    </label>
                    <Input
                        id="presale-amount"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        max={maxPerWallet}
                        placeholder={`0.0 ${currency}`}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="bg-white/5 border-white/10 text-white text-lg font-mono"
                        data-testid="presale-amount-input"
                    />
                    <div className="text-xs text-white/40 mt-1">
                        {t('presale.maxPerWallet', { max: maxPerWallet, ccy: currency })}
                    </div>
                </div>

                {/* You receive */}
                <div className="p-4 rounded-xl bg-white/[0.04] border border-white/10 space-y-2">
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-white/60">{t('presale.youReceive')}</span>
                        <span className="font-mono text-xl font-black text-[#FFD700]" data-testid="presale-b4p-out">
                            {totalB4P.toLocaleString()} B4P
                        </span>
                    </div>
                    {isWhitelisted && (
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-[#00FF66] flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> {t('presale.bonus30')}
                            </span>
                            <span className="font-mono text-[#00FF66]">+{bonusB4P.toLocaleString()} B4P</span>
                        </div>
                    )}
                    <div className="flex justify-between items-center text-xs pt-1 border-t border-white/10">
                        <span className="text-white/50 flex items-center gap-1">
                            <Ticket className="w-3 h-3" /> {t('presale.lotteryTickets')}
                        </span>
                        <span className="font-mono text-[#9B6FE8] font-bold" data-testid="presale-tickets-preview">
                            {tickets} {t('presale.tickets')}
                        </span>
                    </div>
                </div>

                <Button
                    onClick={handleBuy}
                    disabled={submitting || numericAmount <= 0 || (maxPerWallet && numericAmount > maxPerWallet)}
                    className="w-full bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-bold py-6 text-lg btn-press"
                    data-testid="presale-buy-button"
                >
                    {submitting ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('presale.processing')}</>
                    ) : !isConnected ? (
                        <><Wallet className="w-5 h-5 mr-2" /> {t('presale.connectFirst')}</>
                    ) : (
                        <>{t('presale.buyCta', { ccy: currency })} <ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                </Button>

                <div className="text-xs text-white/50 text-center">
                    {t('presale.vestingNote')}
                </div>
            </CardContent>
        </Card>
    );
}

function FoundersLeaderboard({ leaderboard, userAddress }) {
    const { t } = useTranslation();
    if (!leaderboard?.length) {
        return (
            <Card className="bg-white/[0.04] border-white/10">
                <CardContent className="p-10 text-center">
                    <Crown className="w-10 h-10 text-[#FFD700]/40 mx-auto mb-3" />
                    <p className="text-white/60">{t('presale.lbEmpty')}</p>
                </CardContent>
            </Card>
        );
    }
    return (
        <div className="space-y-2" data-testid="presale-leaderboard">
            {leaderboard.map((row) => {
                const style = TIER_STYLES[row.tier] || TIER_STYLES.early_holder;
                const Icon = style.icon;
                const isMe = userAddress && row.address.toLowerCase() === userAddress.toLowerCase();
                return (
                    <Card
                        key={row.address}
                        className={`${isMe ? 'bg-[#00FF66]/10 border-[#00FF66]/40' : 'bg-white/[0.04] border-white/10'} hover:border-[#FFD700]/30 transition-colors`}
                        data-testid={`lb-row-${row.rank}`}
                    >
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-mono text-lg font-black ${row.rank <= 3 ? 'bg-[#FFD700]/15 text-[#FFD700]' : 'bg-white/5 text-white/70'}`}>
                                #{row.rank}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-mono text-white text-sm">{formatAddr(row.address)}</span>
                                    {isMe && <Badge className="bg-[#00FF66]/20 text-[#00FF66] text-[10px]">{t('common.you')}</Badge>}
                                    <Badge
                                        className={`text-[10px] ${style.bg} ${style.border}`}
                                        style={{ color: style.color }}
                                    >
                                        <Icon className="w-2.5 h-2.5 mr-1" />
                                        {t(style.labelKey)}
                                    </Badge>
                                </div>
                                <div className="text-xs text-white/50 flex items-center gap-3 font-mono">
                                    <span>{row.total_eth > 0 && `${row.total_eth} ETH`}</span>
                                    <span>{row.total_usdc > 0 && `${row.total_usdc.toLocaleString()} USDC`}</span>
                                    <span className="flex items-center gap-1">
                                        <Ticket className="w-3 h-3 text-[#9B6FE8]" />
                                        {row.tickets}
                                    </span>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-mono font-black text-[#FFD700]">{row.total_b4p.toLocaleString()}</div>
                                <div className="text-[10px] text-white/40 uppercase">B4P</div>
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}

export default function PresalePage() {
    const { t } = useTranslation();
    const { address, isConnected } = useWeb3();

    const [config, setConfig] = useState(null);
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    async function refresh() {
        try {
            const [cfgRes, statsRes, lbRes] = await Promise.all([
                fetch(`${API}/api/presale/config`).then((r) => r.json()),
                fetch(`${API}/api/presale/stats`).then((r) => r.json()),
                fetch(`${API}/api/presale/leaderboard?limit=50`).then((r) => r.json()),
            ]);
            setConfig(cfgRes);
            setStats(statsRes);
            setLeaderboard(lbRes.leaderboard || []);
            if (address) {
                const userRes = await fetch(`${API}/api/presale/user/${address}`).then((r) => r.json());
                setUserInfo(userRes);
            }
        } catch (e) {
            console.error('presale fetch failed', e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 30_000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address]);

    const progressPct = stats?.progress_pct || 0;

    return (
        <div className="min-h-screen" data-testid="presale-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/10 via-transparent to-[#0066CC]/15" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/30 mb-6">
                        <Flame className="w-3.5 h-3.5 text-[#FFD700]" />
                        <span className="text-[#FFD700] text-xs font-bold uppercase tracking-widest">
                            {t('presale.heroBadge')}
                        </span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-3">
                        B4P <span className="text-[#FFD700]">{t('presale.heroTitle')}</span>
                    </h1>
                    <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
                        {t('presale.heroDesc')}
                    </p>
                    <CountdownToLaunch />
                </div>
            </section>

            {/* KPIs */}
            <section className="py-12 border-b border-white/10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        <StatBox
                            icon={Coins}
                            label={t('presale.statRaised')}
                            value={loading ? '…' : `$${(stats?.raised_usd || 0).toLocaleString()}`}
                            accent="#FFD700"
                            testId="presale-stat-raised"
                        />
                        <StatBox
                            icon={Sparkles}
                            label={t('presale.statSold')}
                            value={loading ? '…' : `${((stats?.total_b4p_sold || 0) / 1_000_000).toFixed(2)}M`}
                            accent="#00FF66"
                            testId="presale-stat-sold"
                        />
                        <StatBox
                            icon={Users}
                            label={t('presale.statContributors')}
                            value={loading ? '…' : stats?.total_contributors || 0}
                            accent="#9B6FE8"
                            testId="presale-stat-contributors"
                        />
                        <StatBox
                            icon={Ticket}
                            label={t('presale.statTickets')}
                            value={loading ? '…' : (stats?.total_lottery_tickets || 0).toLocaleString()}
                            accent="#0066CC"
                            testId="presale-stat-tickets"
                        />
                    </div>

                    {/* Progress bar */}
                    <div className="mt-6">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-white/60">{t('presale.progressTitle')}</span>
                            <span className="font-mono text-[#FFD700] font-bold">{progressPct}% / 100%</span>
                        </div>
                        <Progress value={progressPct} className="h-3 bg-white/10" data-testid="presale-progress" />
                        <div className="flex justify-between text-xs text-white/40 mt-2 font-mono">
                            <span>${(stats?.raised_usd || 0).toLocaleString()}</span>
                            <span>{t('presale.softTarget')} ${(stats?.soft_target_usd || 0).toLocaleString()}</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* MAIN GRID */}
            <section className="py-12">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left: Buy form */}
                    <div className="lg:col-span-1 space-y-6">
                        <BuyForm config={config} userInfo={userInfo} onPurchased={refresh} />

                        {/* My contribution */}
                        {isConnected && userInfo && userInfo.total_b4p > 0 && (
                            <Card className="bg-white/[0.04] border-white/10" data-testid="presale-my-info">
                                <CardHeader>
                                    <CardTitle className="text-base text-white flex items-center gap-2">
                                        <Crown className="w-4 h-4 text-[#FFD700]" />
                                        {t('presale.myContribution')}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-white/60">{t('presale.rank')}</span>
                                        <span className="font-mono font-bold text-[#FFD700]">#{userInfo.rank || '–'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-white/60">{t('presale.tier')}</span>
                                        <Badge className="text-xs" style={{ color: TIER_STYLES[userInfo.tier]?.color }}>
                                            {t(TIER_STYLES[userInfo.tier]?.labelKey || 'presale.tierEarly')}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-white/60">{t('presale.totalB4p')}</span>
                                        <span className="font-mono font-bold text-white">{userInfo.total_b4p.toLocaleString()} B4P</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-white/60 flex items-center gap-1">
                                            <Ticket className="w-3 h-3 text-[#9B6FE8]" />
                                            {t('presale.myTickets')}
                                        </span>
                                        <span className="font-mono font-bold text-[#9B6FE8]">{userInfo.lottery_tickets}</span>
                                    </div>
                                    {userInfo.whitelisted && (
                                        <div className="pt-2 border-t border-white/10">
                                            <Badge className="bg-[#00FF66]/10 text-[#00FF66] text-xs w-full justify-center py-1">
                                                <Check className="w-3 h-3 mr-1" />
                                                {t('presale.whitelistedBonus')}
                                            </Badge>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Lottery info */}
                        <Card className="bg-gradient-to-br from-[#9B6FE8]/10 to-transparent border-[#9B6FE8]/20" data-testid="presale-lottery-card">
                            <CardHeader>
                                <CardTitle className="text-base text-white flex items-center gap-2">
                                    <Gift className="w-4 h-4 text-[#9B6FE8]" />
                                    {t('presale.lotteryTitle')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm text-white/70 space-y-3">
                                <p>{t('presale.lotteryDesc')}</p>
                                <div className="p-3 rounded-lg bg-[#9B6FE8]/5 border border-[#9B6FE8]/20 space-y-1">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-white/50">{t('presale.lotteryTicketEth')}</span>
                                        <span className="font-mono text-[#9B6FE8]">0.1 ETH = 1 🎫</span>
                                    </div>
                                    <div className="flex justify-between text-xs">
                                        <span className="text-white/50">{t('presale.lotteryTicketUsdc')}</span>
                                        <span className="font-mono text-[#9B6FE8]">400 USDC = 1 🎫</span>
                                    </div>
                                </div>
                                <div className="text-xs text-[#FFD700]">
                                    🏆 {t('presale.lotteryPrize')}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right: Leaderboard */}
                    <div className="lg:col-span-2">
                        <Card className="bg-white/[0.02] border-white/10">
                            <CardHeader>
                                <CardTitle className="text-xl text-white flex items-center gap-2">
                                    <Crown className="w-5 h-5 text-[#FFD700]" />
                                    {t('presale.foundersTitle')}
                                </CardTitle>
                                <p className="text-sm text-white/50">{t('presale.foundersDesc')}</p>
                            </CardHeader>
                            <CardContent>
                                <FoundersLeaderboard leaderboard={leaderboard} userAddress={address} />
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 border-t border-white/10 bg-gradient-to-br from-[#FFD700]/5 to-transparent">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <TrendingUp className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
                    <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">{t('presale.finalCtaTitle')}</h2>
                    <p className="text-white/60 mb-8">{t('presale.finalCtaDesc')}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/tokenomics">
                            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/5 px-8 py-6">
                                {t('presale.viewTokenomics')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                        <Link to="/faq">
                            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/5 px-8 py-6">
                                {t('presale.readFaq')}
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
